import { memo } from "react";
import { BdcShipmentHeader } from "../../../types/bdc.type";
import { ControlSheet } from "../controlSheet";
import { lineText, pcs, shortSkuName, thaiDate } from "../format";

/** "ใบควบคุมเอกสารผู้ผลิตสินค้าเบเกอรี่ WB05(คลัง BDC รังสิต คลังดี) 1" — the head the customer prints. */
export const routeSummaryTitle = (header: BdcShipmentHeader) =>
  `ใบควบคุมเอกสารผู้ผลิตสินค้าเบเกอรี่ ${header.dc_code}(${header.dc_name}) ${header.round ?? ""}`.trim();

/**
 * ใบคุมสรุปตามสาย (W8) — for the delivery driver: one row per สาย with each product's delivered
 * pieces, then the grand total, full cases and remainder. No store rows. Built from the same
 * pivot as the per-store sheet, so the two always agree.
 */
function RouteSummaryView({
  sheet,
  header,
  sellerName,
}: Readonly<{ sheet: ControlSheet; header: BdcShipmentHeader; sellerName?: string | null }>) {
  return (
    <div className="bdc-route-summary">
      <div style={{ textAlign: "center", fontWeight: 600, fontSize: 13 }}>{routeSummaryTitle(header)}</div>
      <div style={{ textAlign: "center", fontSize: 12, marginBottom: 8 }}>
        บริษัทผู้ผลิตสินค้า {sellerName || "บริษัท เอ็น.ซี.เอส. โกลด์เบรด จำกัด"} · วันที่ส่งสินค้า{" "}
        {thaiDate(header.delivery_date)} · PO {header.po_number}
      </div>
      <table className="bdc-t bdc-pv">
        <thead>
          <tr>
            <th className="c">สายที่</th>
            <th className="n">ร้าน</th>
            {sheet.columns.map((column) => (
              <th key={column.barcode} className="sk" title={`${column.item_code} ${column.name}`}>
                {shortSkuName(column.name)}
              </th>
            ))}
            <th className="n">ชิ้น</th>
          </tr>
        </thead>
        <tbody>
          {sheet.lines.map((line) => (
            <tr key={String(line.line_no)}>
              <td className="c">{lineText(line.line_no)}</td>
              <td className="n sub">{pcs(line.rows.length)}</td>
              {line.subtotal.map((qty, i) => (
                <td key={i} className={qty ? "q" : "q z"}>
                  {qty || "·"}
                </td>
              ))}
              <td className="q">
                <b>{pcs(line.total)}</b>
              </td>
            </tr>
          ))}
          <tr className="ls">
            <td className="c">รวม {pcs(sheet.lines.length)} สาย</td>
            <td className="n">{pcs(sheet.storeCount)}</td>
            {sheet.grandTotal.map((qty, i) => (
              <td key={i} className="q">
                {pcs(qty)}
              </td>
            ))}
            <td className="q">{pcs(sheet.grandTotalPieces)}</td>
          </tr>
          <tr className="cr">
            <td className="c">ลัง</td>
            <td />
            {sheet.fullCases.map((value, i) => (
              <td key={i} className="q">
                {value}
              </td>
            ))}
            <td />
          </tr>
          <tr className="cr">
            <td className="c">เศษ</td>
            <td />
            {sheet.remainder.map((value, i) => (
              <td key={i} className="q">
                {value}
              </td>
            ))}
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default memo(RouteSummaryView);
